"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurationGroups } from "@/hooks/waves/useWaveCurationGroups";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(95);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const submissionExperience = t2;
  const mountedRef = useRef(true);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      mountedRef.current = false;
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const [isCreateDropOpen, setIsCreateDropOpen] = useState(false);
  const [isMemesCreateOpen, setIsMemesCreateOpen] = useState(false);
  const [isCurationDropModalOpen, setIsCurationDropModalOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(undefined);
  const [maxPrice, setMaxPrice] = useState(undefined);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[6] !== isCurationWave || $[7] !== isLoggedIn || $[8] !== participation || $[9] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[6] = isCurationWave;
    $[7] = isLoggedIn;
    $[8] = participation;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    canCreateDrop
  } = t6;
  const showToggleableDropInput = submissionExperience !== WaveSubmissionExperience.MEMES_LEGACY && submissionExperience !== WaveSubmissionExperience.CURATION_LEGACY && isCreateDropOpen;
  let t7;
  if ($[11] !== canCreateDrop || $[12] !== submissionExperience) {
    t7 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.MEMES_LEGACY) {
        setIsMemesCreateOpen(true);
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY) {
        if (!canCreateDrop) {
          return;
        }
        setIsCurationDropModalOpen(true);
        return;
      }
      setIsCreateDropOpen(true);
    };
    $[11] = canCreateDrop;
    $[12] = submissionExperience;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onCreateDrop = t7;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  let t8;
  if ($[14] !== isCurationWave) {
    t8 = value => value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT || isCurationWave && value === WaveDropsLeaderboardSort.PRICE;
    $[14] = isCurationWave;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, t8);
  const t9 = wave.wave.type !== ApiWaveType.Chat;
  let t10;
  if ($[16] !== t9 || $[17] !== wave.id) {
    t10 = {
      waveId: wave.id,
      enabled: t9
    };
    $[16] = t9;
    $[17] = wave.id;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    data: t11,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurationGroups(t10);
  let t12;
  if ($[19] !== t11) {
    t12 = t11 === undefined ? [] : t11;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const curationGroups = t12;
  let t13;
  if ($[21] !== searchParams) {
    t13 = searchParams.get("curated_by_group");
    $[21] = searchParams;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const rawCuratedByGroupId = t13;
  const curationGroupIdSet = new Set(curationGroups.map(_temp));
  let t14;
  bb0: {
    if (!rawCuratedByGroupId) {
      t14 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t14 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t14 = rawCuratedByGroupId;
      break bb0;
    }
    t14 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t14;
  let t15;
  bb1: {
    const hasPriceFilter = typeof minPrice === "number" || typeof maxPrice === "number";
    if (isCurationWave && (hasPriceFilter || sort === WaveDropsLeaderboardSort.PRICE)) {
      t15 = "ETH";
      break bb1;
    }
    t15 = undefined;
  }
  const priceCurrency = t15;
  let t16;
  if ($[23] !== pathname || $[24] !== router || $[25] !== searchParams) {
    t16 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curated_by_group", groupId);
      } else {
        nextParams.delete("curated_by_group");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[23] = pathname;
    $[24] = router;
    $[25] = searchParams;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const updateCurationGroupInUrl = t16;
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = t18 => {
      const {
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      } = t18;
      setMinPrice(nextMinPrice);
      setMaxPrice(nextMaxPrice);
    };
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const updatePriceRange = t17;
  let t18;
  bb2: {
    if (!isMemesWave) {
      t18 = viewMode;
      break bb2;
    }
    if (viewMode === "grid_content_only") {
      t18 = "grid";
      break bb2;
    }
    t18 = viewMode;
  }
  const effectiveViewMode = t18;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    let t19;
    if ($[28] !== curatedByGroupId || $[29] !== maxPrice || $[30] !== minPrice || $[31] !== onCreateDrop || $[32] !== priceCurrency || $[33] !== sort || $[34] !== wave) {
      t19 = <WaveLeaderboardDrops wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={onCreateDrop} />;
      $[28] = curatedByGroupId;
      $[29] = maxPrice;
      $[30] = minPrice;
      $[31] = onCreateDrop;
      $[32] = priceCurrency;
      $[33] = sort;
      $[34] = wave;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    leaderboardContent = t19;
  } else {
    if (!isMemesWave) {
      const t19 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t20;
      if ($[36] !== curatedByGroupId || $[37] !== maxPrice || $[38] !== minPrice || $[39] !== onDropClick || $[40] !== priceCurrency || $[41] !== sort || $[42] !== t19 || $[43] !== wave) {
        t20 = <WaveLeaderboardGrid wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} mode={t19} onDropClick={onDropClick} />;
        $[36] = curatedByGroupId;
        $[37] = maxPrice;
        $[38] = minPrice;
        $[39] = onDropClick;
        $[40] = priceCurrency;
        $[41] = sort;
        $[42] = t19;
        $[43] = wave;
        $[44] = t20;
      } else {
        t20 = $[44];
      }
      leaderboardContent = t20;
    } else {
      let t19;
      if ($[45] !== curatedByGroupId || $[46] !== maxPrice || $[47] !== minPrice || $[48] !== onDropClick || $[49] !== priceCurrency || $[50] !== sort || $[51] !== wave) {
        t19 = <WaveLeaderboardGallery wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onDropClick={onDropClick} />;
        $[45] = curatedByGroupId;
        $[46] = maxPrice;
        $[47] = minPrice;
        $[48] = onDropClick;
        $[49] = priceCurrency;
        $[50] = sort;
        $[51] = wave;
        $[52] = t19;
      } else {
        t19 = $[52];
      }
      leaderboardContent = t19;
    }
  }
  let t19;
  if ($[53] !== wave) {
    t19 = <WaveLeaderboardTime wave={wave} />;
    $[53] = wave;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== setViewMode) {
    t20 = mode => setViewMode(mode);
    $[55] = setViewMode;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== setSort) {
    t21 = s => setSort(s);
    $[57] = setSort;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const t22 = curatedByGroupId ?? null;
  const t23 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  const t24 = isCurationWave ? updatePriceRange : undefined;
  let t25;
  if ($[59] !== curationGroups || $[60] !== effectiveViewMode || $[61] !== maxPrice || $[62] !== minPrice || $[63] !== onCreateDrop || $[64] !== sort || $[65] !== t20 || $[66] !== t21 || $[67] !== t22 || $[68] !== t23 || $[69] !== t24 || $[70] !== wave) {
    t25 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t20} onCreateDrop={onCreateDrop} onSortChange={t21} curationGroups={curationGroups} curatedByGroupId={t22} onCurationGroupChange={t23} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={t24} /></div>;
    $[59] = curationGroups;
    $[60] = effectiveViewMode;
    $[61] = maxPrice;
    $[62] = minPrice;
    $[63] = onCreateDrop;
    $[64] = sort;
    $[65] = t20;
    $[66] = t21;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
    $[70] = wave;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== showToggleableDropInput || $[73] !== wave) {
    t26 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} /></motion.div>;
    $[72] = showToggleableDropInput;
    $[73] = wave;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t26) {
    t27 = <AnimatePresence>{t26}</AnimatePresence>;
    $[75] = t26;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== isMemesCreateOpen || $[78] !== submissionExperience || $[79] !== wave) {
    t28 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && isMemesCreateOpen && <MemesArtSubmissionModal isOpen={isMemesCreateOpen} wave={wave} onClose={() => setIsMemesCreateOpen(false)} />;
    $[77] = isMemesCreateOpen;
    $[78] = submissionExperience;
    $[79] = wave;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== isCurationDropModalOpen || $[82] !== submissionExperience || $[83] !== wave) {
    t29 = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isCurationDropModalOpen && <WaveLeaderboardCurationDropModal isOpen={isCurationDropModalOpen} wave={wave} onClose={() => setIsCurationDropModalOpen(false)} />;
    $[81] = isCurationDropModalOpen;
    $[82] = submissionExperience;
    $[83] = wave;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== leaderboardContent || $[86] !== t27 || $[87] !== t28 || $[88] !== t29) {
    t30 = <div className="tw-min-w-0">{t27}{t28}{t29}{leaderboardContent}</div>;
    $[85] = leaderboardContent;
    $[86] = t27;
    $[87] = t28;
    $[88] = t29;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] !== leaderboardViewStyle || $[91] !== t19 || $[92] !== t25 || $[93] !== t30) {
    t31 = <div className="tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t19}{t25}{t30}</div>;
    $[90] = leaderboardViewStyle;
    $[91] = t19;
    $[92] = t25;
    $[93] = t30;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  return t31;
};
export default MyStreamWaveLeaderboard;
function _temp(group) {
  return group.id;
}